/**
 * G12 / V32: the at-a-glance overview. One tile per project, built from the same Lanes view model
 * the project tab renders (so a tile can never disagree with its tab): needs-you count, running
 * agents with their live line, what landed last, the next digest item and health.
 */
import type { ChangelogItem, DigestReport } from '@viko/protocol'
import {
  buildLanesModel,
  type LaneAgent,
  type LanesInput,
  type LanesModel,
  type LanesState,
} from './lanes-model.js'

export type Health = 'blocked' | 'degraded' | 'running' | 'green' | 'idle'

export interface OverviewAgent {
  runId: string
  label: string
  line: string
  streaming: boolean
  waiting: boolean
}

export interface OverviewTile {
  projectId: string
  name: string
  health: Health
  healthLabel: string
  needs: number
  running: number
  queued: number
  landedToday: number
  /** Up to three agents, waiting ones first; `moreAgents` counts the rest (never hidden). */
  agents: OverviewAgent[]
  moreAgents: number
  /** The first agent's live "now" line. */
  liveLine?: string
  lastLanded?: { at: number; title: string; taskId: string }
  /** Newest changelog/digest item the user has not been shown yet. */
  nextDigest?: { title: string; at: number }
  /** Why the tile is degraded (hub connection or offline machines). */
  degradedReason?: string
}

export interface Overview {
  tiles: OverviewTile[]
  totals: { projects: number; needs: number; running: number }
}

export interface OverviewInput extends Omit<LanesInput, 'project'> {
  projects: LanesInput['project'][]
  digests?: readonly DigestReport[]
  /** The app-wide needs-you total (lib/needs-you.ts), incl. asks outside any project. */
  needsYouTotal?: number
}

const HEALTH_LABEL: Record<Health, string> = {
  blocked: 'Needs you',
  degraded: 'Degraded',
  running: 'Running',
  green: 'All green',
  idle: 'Idle',
}

export function healthOf(state: LanesState, degraded: LanesModel['degraded']): Health {
  if (state === 'needs-you') return 'blocked'
  if (degraded.hub || degraded.machines.length) return 'degraded'
  if (state === 'busy') return 'running'
  if (state === 'green') return 'green'
  return 'idle'
}

const byAttention = (a: LaneAgent, b: LaneAgent) => Number(b.waiting) - Number(a.waiting)

function lastLanded(model: LanesModel): OverviewTile['lastLanded'] {
  let best: OverviewTile['lastLanded']
  for (const c of model.cards)
    if (c.landedAt && (!best || c.landedAt > best.at))
      best = { at: c.landedAt, title: c.title, taskId: c.id }
  return best
}

/** The newest digest item for the project (digest sections first, then nothing). */
export function nextDigestItem(
  digests: readonly DigestReport[] | undefined,
  projectId: string,
): OverviewTile['nextDigest'] {
  let best: ChangelogItem | undefined
  for (const d of digests ?? [])
    for (const s of d.sections)
      if (s.projectId === projectId) for (const i of s.items) if (!best || i.at > best.at) best = i
  return best ? { title: best.title, at: best.at } : undefined
}

export function tileFromModel(model: LanesModel, digests?: readonly DigestReport[]): OverviewTile {
  const agents = model.lanes.flatMap((l) => l.agents).sort(byAttention)
  const health = healthOf(model.state, model.degraded)
  const first = agents.find((a) => !a.waiting) ?? agents[0]
  const landed = lastLanded(model)
  const next = nextDigestItem(digests, model.projectId)
  const reason = model.degraded.hub
    ? 'Hub connection lost; live lines are frozen'
    : model.degraded.machines.length
      ? `${model.degraded.machines.join(' and ')} offline`
      : undefined
  return {
    projectId: model.projectId,
    name: model.name,
    health,
    healthLabel: HEALTH_LABEL[health],
    needs: model.counts.needs,
    running: model.counts.running,
    queued: model.counts.queued,
    landedToday: model.counts.landedToday,
    agents: agents.slice(0, 3).map((a) => ({
      runId: a.runId,
      label: a.label,
      line: a.line,
      streaming: a.streaming,
      waiting: a.waiting,
    })),
    moreAgents: Math.max(0, agents.length - 3),
    ...(first ? { liveLine: first.line } : {}),
    ...(landed ? { lastLanded: landed } : {}),
    ...(next ? { nextDigest: next } : {}),
    ...(reason ? { degradedReason: reason } : {}),
  }
}

const RANK: Record<Health, number> = { blocked: 0, degraded: 1, running: 2, green: 3, idle: 4 }

export function buildOverview(input: OverviewInput): Overview {
  const { projects, digests, needsYouTotal, ...rest } = input
  const tiles = projects
    .map((project) => tileFromModel(buildLanesModel({ ...rest, project }), digests))
    // Blocked first (most waiting first), then by recent life; stable by name.
    .sort(
      (a, b) =>
        RANK[a.health] - RANK[b.health] ||
        b.needs - a.needs ||
        b.running - a.running ||
        a.name.localeCompare(b.name),
    )
  return {
    tiles,
    totals: {
      projects: tiles.length,
      needs: needsYouTotal ?? tiles.reduce((n, t) => n + t.needs, 0),
      running: tiles.reduce((n, t) => n + t.running, 0),
    },
  }
}
